import { Columns, CompareNumericAnalysisV2 } from 'api/data/analyses';
import { isAnalysisInputValid } from 'helpers/analysis';
import { VariablesData } from 'store/data/variables';
import { ActionTypes, VariablesDataSelectItems } from 'store/data/analyses';

import { MediaQueries } from 'environment';
import { CompareNumericView } from './CompareNumericView';
import { CompareNumericConfig } from './CompareNumericConfig';

import { InvalidAnalysis } from '..';
import { AnalysisContainer, AnalysisViewContainer } from '../UI';
import {
	useAnalysesActiveColum,
	useFullscreenAnalysis,
	useFetchAnalysis,
	useTranslation
} from 'hooks/store';
import { useMediaQuery } from 'hooks/utils';
import { withMemo } from 'helpers/HOCs';
import { Loader } from 'components/UI/Loader';

interface Props {
	analysis: CompareNumericAnalysisV2;
	variablesData: VariablesData;
	variablesDataSelectItems: VariablesDataSelectItems;
	exporting: boolean;
}

function Component({ analysis, variablesData, variablesDataSelectItems, exporting }: Props) {
	const { translate } = useTranslation();
	const validInput =
		(analysis.input.variables.categoryVariable ||
			analysis.input.variables.categoryVariableTwo) &&
		(analysis.input.variables.exploreVariable || analysis.input.variables.exploreVariableTwo);

	const { loading } = useFetchAnalysis({
		id: analysis.id,
		actionType: ActionTypes.GET_COMPARE_NUMERIC_V2,
		...(validInput
			? { refetchDependencies: ['input.variables'] }
			: { refetchDependencies: [] }),
		initialFetchDependency: null
	});
	const [activeColumn] = useAnalysesActiveColum();
	const analysisValidInput = isAnalysisInputValid(analysis);
	const isMobileDevice = useMediaQuery(
		`only screen and ${MediaQueries.minWidth.xs} and ${MediaQueries.maxWidth.sm}`
	);

	const [fullscreen, setFullscreenAnalysis] = useFullscreenAnalysis();

	const handleFullScreen = () => {
		if (
			(activeColumn !== Columns.OneColumn && !isMobileDevice) ||
			(activeColumn === Columns.OneColumn && isMobileDevice)
		) {
			setFullscreenAnalysis({ fullscreen: analysis.id });
		}
	};

	if (analysisValidInput) {
		return (
			<AnalysisContainer onClick={() => handleFullScreen()} activeColumn={activeColumn}>
				{!loading ? (
					<CompareNumericView
						analysis={analysis}
						variablesData={variablesData}
						exporting={exporting}
						loading={loading}
					/>
				) : (
					<AnalysisViewContainer isConfigPanelOpen={analysis.options.configPanel.open}>
						<Loader primary />
					</AnalysisViewContainer>
				)}
				{(analysis.options.configPanel.open &&
					activeColumn === Columns.OneColumn &&
					!isMobileDevice) ||
				(analysis.options.configPanel.open && fullscreen) ? (
					<CompareNumericConfig
						analysis={analysis}
						variablesDataSelectItems={variablesDataSelectItems}
						loading={loading}
					/>
				) : null}
			</AnalysisContainer>
		);
	}

	return (
		<AnalysisContainer invalid>
			<InvalidAnalysis
				{...(!analysisValidInput
					? {
							headerMessage: translate(
								dict => dict.analysis.errors.invalidAnalysis.selectInputHeader
							)
					  }
					: {})}
				{...(!analysisValidInput
					? {
							bodyMessage: translate(
								dict => dict.analysis.errors.invalidAnalysis.selectInputBody
							)
					  }
					: {})}
				activeColumn={activeColumn}
				fullScreen={!!fullscreen}
				isConfigPanelOpen={analysis.options.configPanel.open}
				numeric
			/>
			{(analysis.options.configPanel.open &&
				activeColumn === Columns.OneColumn &&
				!isMobileDevice) ||
			(analysis.options.configPanel.open && (analysis.activeTab === 1 || fullscreen)) ? (
				<CompareNumericConfig
					analysis={analysis}
					variablesDataSelectItems={variablesDataSelectItems}
					loading={loading}
				/>
			) : null}
		</AnalysisContainer>
	);
}

export const CompareNumeric = withMemo(Component, [
	'analysis',
	'variablesData',
	'variablesDataSelectItems',
	'exporting'
]);
